/* eslint-disable eqeqeq */

import './Suggest.css';
import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import Endpoints, { SearchOrderBy } from '../../endpoints';
import { Route, Switch } from 'react-router';
import CreateSuggestion from './CreateSuggestion';
import SuggestionList from './SuggestionsList';
import ViewSuggestion from './ViewSuggestion';
import { Caste, User } from 'App';
import { Suggestion, boolqToNum, SuggestionStatus, SuggestionStats } from './Models';
import Header from './Header';

interface Props {
    sId?: string | undefined;
    user: User | null;
}

interface State {
    suggestions: Suggestion[];
    page: number;
    loaded: boolean;
    viewStatus: SuggestionStatus | null;
    stats: SuggestionStats;
    showBack: boolean;
    showCreate: boolean;
}

export default class Suggest extends React.Component<Props & RouteComponentProps, State> {

    constructor(props: Props & RouteComponentProps) {
        super(props);

        const pathData = this.getPathData(this.props.location.pathname);
        this.state = {
            suggestions: [],
            viewStatus: 'OPEN',
            page: 0,
            loaded: false,
            stats: {
                open: 0,
                rejected: 0,
                completed: 0
            },
            showBack: pathData.showBack,
            showCreate: pathData.showCreate,
        };

        this.open = this.open.bind(this);
        this.upvote = this.upvote.bind(this);
        this.downvote = this.downvote.bind(this);
        this.onCommentPosted = this.onCommentPosted.bind(this);
        this.onCommentDeleted = this.onCommentDeleted.bind(this);
        this.onForceSuggestionState = this.onForceSuggestionState.bind(this);
        this.rejectSuggestion = this.rejectSuggestion.bind(this);
        this.completeSuggestion = this.completeSuggestion.bind(this);
        this.deleteSuggestion = this.deleteSuggestion.bind(this);
        this.reopenSuggestion = this.reopenSuggestion.bind(this);
        this.handleSuggestionStatusChange = this.handleSuggestionStatusChange.bind(this);
        this.handleSuggestionDelete = this.handleSuggestionDelete.bind(this);
        this.getAllSuggestions = this.getAllSuggestions.bind(this);
        this.reloadSuggestions = this.reloadSuggestions.bind(this);
        this.handleSearch = this.handleSearch.bind(this);
        this.handleOpenCreate = this.handleOpenCreate.bind(this);
        this.handleOpenMain = this.handleOpenMain.bind(this);
        this.createSuggestion = this.createSuggestion.bind(this);

        this.props.history.listen((data) => {
            const path = data.pathname;
            this.handlePath(path);
        });

        this.reload();
    }

    handlePath(path: string) {
        const data = this.getPathData(path);
        this.setState((prev) => ({
            ...prev,
            showBack: data.showBack,
            showCreate: data.showCreate
        }));
    }

    getPathData(path: string) {
        if (path == '/') {
            return {
                showBack: false,
                showCreate: true,
            };
        } else if (path == '/new') {
            return {
                showBack: true,
                showCreate: false,
            };
        } else {
            return {
                showBack: true,
                showCreate: false,
            };
        }
    }

    reload() {
        this.reloadSuggestions(null, null, this.state.viewStatus);
        this.reloadStats();
    }

    async reloadStats() {
        const [ep, method] = Endpoints.SUGGESTION_STATS();
        await fetch(ep, {
            method
        })
            .then(async (x) => {
                if (!x.ok) {
                    throw new Error(x.statusText);
                }

                const stats = await x.json() as SuggestionStats;
                this.setState({
                    stats
                });
            })
            .catch((err) => console.log(err));
    }

    open(id: number) {
        this.props.history.push('/' + id);
    }

    reloadSuggestions(exclude: number[] | null = null,
        orderBy: SearchOrderBy | null = null,
        status: SuggestionStatus | null = null,
        search: string | null = null) {
        this.getAllSuggestions(exclude, orderBy, status, search)
            .then((sugs) => {
                if (sugs != null) {
                    this.setState({
                        suggestions: sugs,
                        loaded: true,
                        viewStatus: status
                    });
                }
            })
            .catch((err) => console.log(err));
    }

    async getAllSuggestions(exclude: number[] | null = null,
        orderBy: SearchOrderBy | null = null,
        status: SuggestionStatus | null = null,
        search: string | null = null): Promise<Suggestion[] | null> {
        const [ep, method] = Endpoints.SUGGESTION_GETALL(exclude, orderBy, status, search);

        const res = await fetch(ep, { method });
        if (!res.ok) {
            console.log('Error loading suggestions: ' + res.statusText);
            return null;
        } else {
            return await res.json() as Suggestion[];
        }
    }

    async upvote(id: number) {
        const [ep, method] = Endpoints.SUGGESTION_UPVOTE(id);
        fetch(ep, {
            method,
        })
            .then(async (x) => this.handleVote(id, x))
            .catch((err) => console.log(err));
    }

    async downvote(id: number) {
        const [ep, method] = Endpoints.SUGGESTION_DOWNVOTE(id);
        fetch(ep, {
            method,
        })
            .then(async (x) => this.handleVote(id, x))
            .catch((err) => console.log(err));
    }

    back() {
        this.props.history.push('/');
    }

    createSuggestion(title: string, descr: string): void {
        title = title.trim();
        descr = descr.trim();

        const [ep, method] = Endpoints.SUGGESTION_NEW();
        fetch(ep, {
            method,
            body: JSON.stringify({
                title,
                description: descr
            }),
            headers: {
                'Content-Type': 'application/json'
            }
        })
            .then(async (x) => {
                if (!x.ok) {
                    throw new Error(x.statusText);
                }
                const sug = await x.json();
                this.setState((prev) => ({
                    ...prev,
                    suggestions: [...prev.suggestions, sug]
                }));

                this.props.history.push(`/${sug.id}`);
            })
            .catch((err) => {
                console.log(err);
                this.props.history.push('/');
            });
    }

    onCommentPosted(sugId: number, _: number) {
        this.setState((prev) => ({
            ...prev,
            suggestions: prev.suggestions.map((x) => x.id != sugId
                ? x
                : {
                    ...x,
                    commentsCount: x.commentsCount + 1
                })
        }));
    }

    onCommentDeleted(sugId: number, _: number) {
        this.setState((prev) => ({
            ...prev,
            suggestions: prev.suggestions.map((x) => x.id != sugId
                ? x
                : {
                    ...x,
                    commentsCount: x.commentsCount - 1
                })
        }));
    }

    onForceSuggestionState(sugId: number, isUserUpvoted: boolean | null, score: number) {
        this.setState((prev) => ({
            ...prev,
            suggestions: prev.suggestions.map((x) => x.id != sugId ? x : {
                ...x,
                isUserUpvoted,
                score
            })
        }));
    }

    deleteSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_DELETE(sugId);

        fetch(ep, {
            method
        })
            .then((res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                this.handleSuggestionDelete(sugId);
            })
            .catch((err) => console.log(err));
    }

    rejectSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_REJECT(sugId);

        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }
                this.handleSuggestionStatusChange(sugId, 'REJECTED');
            })
            .catch((err) => console.log(err));
    }

    completeSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_COMPLETE(sugId);

        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                this.handleSuggestionStatusChange(sugId, 'COMPLETED');
            })
            .catch((err) => console.log(err));
    }

    reopenSuggestion(sugId: number) {
        const [ep, method] = Endpoints.SUGGESTION_REOPEN(sugId);

        fetch(ep, {
            method
        })
            .then(async (res) => {
                if (!res.ok) {
                    throw new Error(res.statusText);
                }

                this.handleSuggestionStatusChange(sugId, 'OPEN');
            })
            .catch((err) => console.log(err));
    }

    handleBanStateChange: (id: string, banState: boolean) => void = (id, banState) => {
        this.setState((prev) => {
            return {
                ...prev,
                suggestions: prev.suggestions.map((sug) => sug.author.id == id
                    ? {
                        ...sug,
                        author: {
                            ...sug.author,
                            isBanned: banState
                        }
                    }
                    : sug)
            };
        });
    }

    onAdminStateChange: (id: string, caste: Caste) => void = (id, caste) => {
        this.setState((prev) => {
            return {
                ...prev,
                suggestions: prev.suggestions.map((sug) => sug.author.id == id
                    ? {
                        ...sug,
                        author: {
                            ...sug.author,
                            caste: caste
                        }
                    }
                    : sug)
            };
        });
    }

    handleSuggestionStatusChange(sugId: number, status: SuggestionStatus) {
        this.setState((prev) => ({
            ...prev,
            suggestions: prev.suggestions.map((x) => x.id == sugId ? {
                ...x,
                status
            } : x)
        }));
    }

    handleSuggestionDelete(sugId: number) {
        this.setState((prev) => {
            return ({
                ...prev,
                suggestions: prev.suggestions.filter((x) => x.id != sugId),
            });
        });
    }

    handleSearch(search: string | null, sort: number | null, status: SuggestionStatus | null) {
        this.setState({
            loaded: false
        });
        this.reloadSuggestions(null, sort, status, search);
    }

    handleOpenCreate() {
        this.props.history.push('/new');
    }

    handleOpenMain() {
        this.props.history.push('/');
    }

    openContextMenu: ((event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void) = (event) => {
        const elem = document.getElementById('context-menu');
        if (elem == null) {
            return;
        }
        event.preventDefault();
        elem.style.left = event.pageX.toString();
        elem.style.top = event.pageY.toString();
        elem.style.display = 'block';
    }

    render() {
        return (
            <div className="suggest-container">
                <Header
                    stats={this.state.stats}
                    openCreate={this.handleOpenCreate}
                    openMain={this.handleOpenMain}
                    showBack={this.state.showBack}
                    showCreate={this.props.user != null && this.state.showCreate}
                />
                <Switch>
                    <Route
                        exact={true}
                        path="/"
                        render={(props) => <SuggestionList
                            {...props}
                            suggestions={this.state.suggestions}
                            loaded={this.state.loaded}
                            downvote={this.downvote}
                            upvote={this.upvote}
                            open={this.open}
                            stats={this.state.stats}

                            deleteSuggestion={this.deleteSuggestion}
                            rejectSuggestion={this.rejectSuggestion}
                            completeSuggestion={this.completeSuggestion}
                            reopenSuggestion={this.reopenSuggestion}

                            onBanStateChange={this.handleBanStateChange}
                            onAdminStateChange={this.onAdminStateChange}

                            onSearch={this.handleSearch}
                            user={this.props.user}
                            status={this.state.viewStatus}
                        />}
                    />
                    <Route
                        path="/new"
                        render={(props) => <CreateSuggestion
                            {...props}
                            stats={this.state.stats}
                            onCreateSuggestion={this.createSuggestion}
                            user={this.props.user}
                        />}
                    />
                    <Route
                        path="/:sid(\d+)"
                        render={(props) => <ViewSuggestion
                            {...props}
                            back={() => this.back()}
                            upvote={this.upvote}
                            downvote={this.downvote}
                            onCommentPosted={this.onCommentPosted}
                            onCommentDeleted={this.onCommentDeleted}
                            forceSuggestionState={this.onForceSuggestionState}
                            user={this.props.user}
                            stats={this.state.stats}
                            onSuggestionReject={(id: number) => this.handleSuggestionStatusChange(id, 'REJECTED')}
                            onSuggestionReopen={(id: number) => this.handleSuggestionStatusChange(id, 'OPEN')}
                            onSuggestionComplete={(id: number) => this.handleSuggestionStatusChange(id, 'COMPLETED')}
                            onSuggestionDelete={this.handleSuggestionDelete}

                            onBanStateChange={this.handleBanStateChange}
                            onAdminStateChange={this.onAdminStateChange}
                        />}
                    />
                </Switch>
            </div>
        );
    }

    private async handleVote(id: number, x: Response) {
        if (!x.ok) {
            throw new Error(x.statusText);
        }
        const isUserUpvoted = await x.json() as boolean | null;
        this.setState((prev) => ({
            ...prev,
            suggestions: prev.suggestions.map((sug) => sug.id == id ? {
                ...sug,
                isUserUpvoted,
                // subtract old value, add new value
                score: sug.score - boolqToNum(sug.isUserUpvoted) + boolqToNum(isUserUpvoted)
            } : sug)
        }));
    }
}
